import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import FileUploader from "src/shared/components/file-uploader";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import CloseIcon from "@mui/icons-material/Close";
import logo from "src/assets/svg-icons/logo.svg";
import { useNavigate } from "react-router-dom";
import YoutubeViewerBox from "src/pages/add-sales/youtube-viewer-box";
import { useGetPaymentMethods } from "src/api/generic";
import { useRecoilState } from "recoil";
import { notificationsState } from "src/store/notifications";
import { convertObjectToFormData } from "src/utils";
import { useImportBanksMatchingMutation } from "src/api/matching/bank";

interface IProps {}

export const schema = Yup.object().shape({
  bank: Yup.string().required("Required"),
  payment_id: Yup.string().required("Required"),
  bank_file: Yup.string().required("Required"),
});

export interface FormInputs extends Yup.InferType<typeof schema> {}

const AddBankMatchingTransactions: React.FC<IProps> = ({}) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("matching");
  const { t: generalT } = useTranslation("general");
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const [bankFile, setBankFile] = useState<any>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // React hook form
  const {
    formState: { errors },
    setValue,
    control,
    handleSubmit,
  } = useForm<FormInputs>({
    mode: "onBlur",
    resolver: yupResolver(schema),
    defaultValues: {},
  });

  // APIs
  const { mutate, status, error } = useImportBanksMatchingMutation();
  const { data: paymentsData } = useGetPaymentMethods({
    enabled: true,
  });

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    const formDataToSend = convertObjectToFormData({
      payment_method_id: data.payment_id,
      bank: data.bank,
    });
    formDataToSend.append("bank_file", bankFile);

    setIsLoading(true);
    mutate(formDataToSend);
  };

  const handleBankFileUpload = (data: any) => {
    console.log({ data });
    setBankFile(data || undefined);
    setValue("bank_file", "file");
  };

  const goBack = () => {
    navigate("/matching/bank");
  };

  //Add success and error handling
  useEffect(() => {
    if (status === "success") {
      const delayedExecution = setTimeout(() => {
        setNotifications([
          ...notifications,
          {
            type: "success",
            message: generalT("addedSuccessfully"),
          },
        ]);
        setIsLoading(false);
        navigate("/matching/bank");
      }, 30000);

      return () => clearTimeout(delayedExecution);
    } else if (status === "error") {
      setIsLoading(false);
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: error?.data.errors[0].value || generalT("somethingWrong"),
        },
      ]);
    }
  }, [status]);

  return (
    <Box>
      <Box px={{ xs: 3, md: 4 }} pt={4} pb={{ xs: 1, md: 0 }}>
        <img src={logo} alt="" />
        <IconButton
          sx={{ position: "absolute", top: "30px", right: "30px" }}
          onClick={() => {
            navigate("/matching");
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box display={"flex"} justifyContent={"center"} pb={"150px"}>
          <Box maxWidth={"736px"} width={"100%"} px={{ xs: 3, md: 0 }}>
            <Typography fontSize={"26px"} fontWeight={600}>
              {t("addBankTransactions")}
            </Typography>
            <Typography color={"textSecondary"}>{t("letKnowYou")}</Typography>
            <YoutubeViewerBox videoId="djvKcUwSmqw" />

            {/* select bank */}
            <FormControl
              fullWidth
              size="small"
              error={!!errors.bank}
              sx={{ my: "10px" }}
            >
              <InputLabel id="demo-simple-select-label">{t("bank")}</InputLabel>
              <Controller
                name="bank"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    key={field.value}
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    label={t("bank")}
                  >
                    <MenuItem value={"alrajhi"}>{t("alrajhi")}</MenuItem>
                  </Select>
                )}
              />
              <FormHelperText id="my-helper-text">
                {errors.bank?.message}
              </FormHelperText>
            </FormControl>

            {/* select Payment */}
            <FormControl
              fullWidth
              size="small"
              error={!!errors.payment_id}
              sx={{ my: "10px" }}
            >
              <InputLabel id="demo-simple-select-label">
                {generalT("paymentMethod")}
              </InputLabel>
              <Controller
                name="payment_id"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    key={field.value}
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    label={generalT("paymentMethod")}
                  >
                    {paymentsData?.data?.map(({ id, name }) => {
                      return (
                        <MenuItem key={id} value={id}>
                          {name}
                        </MenuItem>
                      );
                    })}
                  </Select>
                )}
              />
              <FormHelperText id="my-helper-text">
                {errors.payment_id?.message}
              </FormHelperText>
            </FormControl>

            {/* Bank File */}
            <Box pt={2}>
              <FileUploader
                label={t("")}
                onFileUpload={(data) => handleBankFileUpload(data)}
                onFileDelete={() => setBankFile(undefined)}
                id="bank_file"
                name="agg_file"
                control={control}
                error={!!errors.bank_file}
                helperText={errors.bank_file?.message}
                isLoading={false}
                value={bankFile}
              />
            </Box>
          </Box>
        </Box>

        {/* submit bar */}
        <Box
          bgcolor={"white"}
          zIndex={2}
          width={"100%"}
          position={"fixed"}
          sx={{ bottom: "0" }}
          boxShadow={"0 -8px 50px -5px rgba(50, 50, 50, 0.1)"}
        >
          <Stack
            direction={"row"}
            justifyContent={"space-around"}
            m={3}
            gap={"10px"}
          >
            <Button onClick={goBack} variant="outlined" color="tertiary">
              {t("back")}
            </Button>

            <Stack direction={"row"} gap={"10px"} alignItems={"center"}>
              <LoadingButton
                variant="contained"
                endIcon={
                  <ArrowForwardIosIcon
                    sx={{
                      height: "15px",
                      transform: i18n.language === "ar" ? "scaleX(-1)" : "",
                    }}
                  />
                }
                sx={{ display: "flex" }}
                loading={isLoading}
                type="submit"
              >
                {generalT("save")}
              </LoadingButton>
            </Stack>
          </Stack>
        </Box>
      </form>
    </Box>
  );
};

export default AddBankMatchingTransactions;
